<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import NavList, { type NavGroup } from '@/components/NavList.vue'
import PageHeader from '@/components/PageHeader.vue'
import { useBackButton } from '@/composables/useBackButton'
import { goBack } from '@/router/goBack'
import { ApiError, getAdminBot, type AdminBotProfile } from '@/api'
import { languageOptions, parseScope, prettyJson, scopesOf } from '@/admin/bot'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'

// The names, descriptions and commands the last profile sync stored. `/admin/bot/profiles` shows what everybody sees in the chosen
// language, `/admin/bot/profiles/chats` the command lists of single admin chats. The language is a toggle on top, kept in `?lang=`.
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const profiles = ref<AdminBotProfile[]>([])
const loading = ref(true)
const loadError = ref('')

const atChats = computed(() => route.name === 'admin-bot-chats')
const parent = computed(() => (atChats.value ? withLanguage('/admin/bot/profiles') : '/admin/bot'))
useBackButton(() => goBack(router, parent.value))

const languages = computed(() => languageOptions(profiles.value))
const language = computed(() => {
  const asked = typeof route.query.lang === 'string' ? route.query.lang : ''
  return languages.value.includes(asked) ? asked : (languages.value[0] ?? '')
})
const scopes = computed(() => scopesOf(profiles.value, language.value))

function withLanguage(path: string): string {
  return language.value === '' ? path : `${path}?lang=${encodeURIComponent(language.value)}`
}

function chooseLanguage(code: string) {
  void router.replace({ query: { ...route.query, lang: code === '' ? undefined : code } })
}

function languageTitle(code: string): string {
  return code === '' ? t('admin.bot.defaultLanguage') : code
}

function text(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : String(value)
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString(locale.value)
  } catch {
    return value
  }
}

function chatTitle(scope: string): string {
  const parsed = parseScope(scope)
  return parsed.kind === 'chat'
    ? t('admin.bot.adminChat', { id: parsed.chatId })
    : t('admin.bot.everybody')
}

function rawOf(profile: AdminBotProfile): string {
  return prettyJson({
    name: profile.raw_get_my_name,
    short_description: profile.raw_get_my_short_description,
    description: profile.raw_get_my_description,
    commands: profile.raw_get_my_commands,
  })
}

const chatLinks = computed<NavGroup[]>(() =>
  scopes.value.chats.length === 0
    ? []
    : [
        {
          key: 'chats',
          items: [
            {
              to: withLanguage('/admin/bot/profiles/chats'),
              icon: '💬',
              title: t('admin.bot.chatSettings'),
              hint: t('admin.bot.chatSettingsHint', { n: scopes.value.chats.length }),
            },
          ],
        },
      ],
)

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    profiles.value = (await getAdminBot()).profiles
  } catch (e) {
    loadError.value = e instanceof ApiError ? e.message : t('admin.bot.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="profiles-page" data-testid="admin-bot-profiles">
    <PageHeader
      :title="atChats ? t('admin.bot.chatSettings') : t('admin.bot.profiles')"
      :hint="atChats ? t('admin.bot.chatSettingsPageHint') : t('admin.bot.profilesPageHint')"
      :back-to="parent"
      :back-label="t('admin.back')"
    />

    <p v-if="loading">{{ t('common.loading') }}</p>
    <template v-else-if="loadError">
      <p class="ui-error">{{ loadError }}</p>
      <button type="button" class="ui-btn" @click="load">{{ t('common.retry') }}</button>
    </template>
    <p v-else-if="languages.length === 0" class="ui-hint" data-testid="bot-empty">
      {{ t('admin.bot.empty') }}
    </p>

    <template v-else>
      <UiGroup>
        <div
          class="ui-segmented languages"
          role="group"
          :aria-label="t('admin.bot.language')"
          data-testid="bot-languages"
        >
          <button
            v-for="code in languages"
            :key="code"
            type="button"
            :aria-pressed="code === language"
            @click="chooseLanguage(code)"
          >
            {{ languageTitle(code) }}
          </button>
        </div>
      </UiGroup>

      <template v-if="!atChats">
        <UiGroup v-if="scopes.everybody">
          <UiList as="section" class="card" data-testid="bot-everybody">
            <dl>
              <div class="field">
                <dt>{{ t('admin.bot.name') }}</dt>
                <dd>{{ text(scopes.everybody.name) }}</dd>
              </div>
              <div class="field">
                <dt>{{ t('admin.bot.shortDescription') }}</dt>
                <dd>{{ text(scopes.everybody.short_description) }}</dd>
              </div>
              <div class="field">
                <dt>{{ t('admin.bot.description') }}</dt>
                <dd>{{ text(scopes.everybody.description) }}</dd>
              </div>
              <div class="field">
                <dt>{{ t('admin.bot.commands') }}</dt>
                <dd>
                  <ul v-if="scopes.everybody.commands?.length" class="commands">
                    <li v-for="command in scopes.everybody.commands" :key="command.command">
                      <code>/{{ command.command }}</code> {{ command.description }}
                    </li>
                  </ul>
                  <template v-else>—</template>
                </dd>
              </div>
            </dl>
            <details class="raw">
              <summary>{{ t('admin.bot.raw') }}</summary>
              <pre>{{ rawOf(scopes.everybody) }}</pre>
            </details>
            <p class="ui-hint">
              {{ t('admin.bot.fetched') }}: {{ formatDate(scopes.everybody.fetched_at) }}
            </p>
          </UiList>
        </UiGroup>
        <p v-else class="ui-hint">{{ t('admin.bot.noEverybody') }}</p>

        <NavList :groups="chatLinks" />
      </template>

      <template v-else>
        <p v-if="scopes.chats.length === 0" class="ui-hint" data-testid="bot-no-chats">
          {{ t('admin.bot.noChats') }}
        </p>
        <UiGroup v-for="profile in scopes.chats" :key="profile.scope">
          <UiList as="section" class="card" data-testid="bot-scope">
            <h4>{{ chatTitle(profile.scope) }}</h4>
            <dl>
              <div class="field">
                <dt>{{ t('admin.bot.commands') }}</dt>
                <dd>
                  <ul v-if="profile.commands?.length" class="commands">
                    <li v-for="command in profile.commands" :key="command.command">
                      <code>/{{ command.command }}</code> {{ command.description }}
                    </li>
                  </ul>
                  <template v-else>—</template>
                </dd>
              </div>
            </dl>
            <details class="raw">
              <summary>{{ t('admin.bot.raw') }}</summary>
              <pre>{{ rawOf(profile) }}</pre>
            </details>
            <p class="ui-hint">
              {{ t('admin.bot.fetched') }}: {{ formatDate(profile.fetched_at) }}
            </p>
          </UiList>
        </UiGroup>
      </template>
    </template>
  </div>
</template>

<style scoped>
/* Works down to a 320 px wide screen: the language toggle wraps, labels sit above their values and everything long wraps. */
.profiles-page {
  min-width: 0;
}

.languages {
  display: flex;
  flex-wrap: wrap;
}

.card {
  padding: 0.25rem 0.85rem 0.6rem;
}

dl,
dd,
ul {
  margin: 0;
  padding: 0;
}

.field {
  padding: 0.45rem 0;
}

.field + .field {
  border-top: 1px solid var(--tg-secondary-bg-color);
}

dt {
  font-size: 0.8rem;
  color: var(--tg-hint-color);
}

dd,
h4 {
  overflow-wrap: anywhere;
}

h4 {
  margin: 0.6rem 0 0.2rem;
  font-size: 0.95rem;
}

.commands {
  list-style: none;
}

.commands li {
  padding: 0.1rem 0;
}

.raw {
  margin-top: 0.4rem;
}

.raw summary {
  font-size: 0.8rem;
  color: var(--tg-hint-color);
  cursor: pointer;
}

.raw pre {
  max-width: 100%;
  margin: 0.3rem 0 0;
  padding: 0.5rem;
  overflow-x: auto;
  border-radius: 8px;
  font-size: 0.75rem;
  background: var(--tg-secondary-bg-color);
}
</style>
